import React, { useState, useEffect, useRef } from 'react';
import { Spin } from 'antd';
import {
  SafetyOutlined,
  InfoCircleOutlined,
  BulbOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { adminAPI } from '../services/api';
import useGlassCard from '../hooks/useGlassCard';
import ProfileCard from '../components/ProfileCard';
import './HomePage.css';

interface HomePageConfig {
  siteNotes: string;
  welcomeContent: string;
}

const defaultFeatures = [
  { emoji: '🚀', text: '一站式数据管理，高效便捷' },
  { emoji: '🔒', text: '安全可靠的数据存储与传输' },
  { emoji: '⚡', text: '极速 API 接口，稳定响应' },
  { emoji: '📊', text: '实时数据统计与可视化' },
  { emoji: '🎯', text: '精准邀请码管理，灵活分配' },
];

const defaultNotes = [
  { icon: <SafetyOutlined />, title: '账号安全', desc: '请妥善保管您的账号信息，不要将密码泄露给他人。' },
  { icon: <InfoCircleOutlined />, title: '合法使用', desc: '请遵守相关法律法规，不得上传违法违规内容。' },
  { icon: <BulbOutlined />, title: '用途规范', desc: '本平台仅供合法业务使用，禁止用于任何非法用途。' },
  { icon: <CheckCircleOutlined />, title: '联系管理员', desc: '如有任何问题或建议，请及时联系管理员。' },
];

const HomePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [config, setConfig] = useState<HomePageConfig>({
    siteNotes: '',
    welcomeContent: '',
  });
  const cardRef = useRef<HTMLDivElement>(null);
  useGlassCard(cardRef);

  const fetchConfig = async () => {
    try {
      const [notesRes, welcomeRes] = await Promise.all([
        adminAPI.getHomepageConfig('site_notes'),
        adminAPI.getHomepageConfig('welcome_content'),
      ]);

      setConfig({
        siteNotes: notesRes.data.status === 200 ? notesRes.data.data.value : '',
        welcomeContent: welcomeRes.data.status === 200 ? welcomeRes.data.data.value : '',
      });
    } catch (error) {
      console.error('获取首页配置失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => setVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="homepage-loading">
        <Spin size="large" />
      </div>
    );
  }

  const parsedWelcomeLines = config.welcomeContent
    ? config.welcomeContent.split('\n').filter((line) => line.trim())
    : [];

  const parsedNoteLines = config.siteNotes
    ? config.siteNotes.split('\n').filter((line) => line.trim())
    : [];

  const features = parsedWelcomeLines.length > 0
    ? parsedWelcomeLines.map((line) => {
        const emojiMatch = line.match(/^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F)\s*(.*)/u);
        if (emojiMatch) {
          return { emoji: emojiMatch[1], text: emojiMatch[2] };
        }
        return { emoji: '✨', text: line };
      })
    : defaultFeatures;

  const notes = parsedNoteLines.length > 0
    ? parsedNoteLines.map((line, index) => {
        const icons = [SafetyOutlined, InfoCircleOutlined, BulbOutlined, CheckCircleOutlined];
        const IconComp = icons[index % icons.length];
        return { icon: <IconComp />, title: line, desc: '' };
      })
    : defaultNotes;

  return (
    <div className="homepage-container">
      <div className="homepage-bg">
        <div className="homepage-bg-orb orb-1"></div>
        <div className="homepage-bg-orb orb-2"></div>
        <div className="homepage-bg-orb orb-3"></div>
      </div>

      <div className="homepage-content">
        <ProfileCard />
        <div
          ref={cardRef}
          className={`home-main-card ${visible ? 'home-main-card-visible' : ''}`}
        >
          <div className="home-two-col">
            <div className="home-col-left">
              <div className="home-col-header">
                <div className="home-welcome-icon">🚀</div>
                <div>
                  <h2 className="home-welcome-title">欢迎来到 Tomato 综合站</h2>
                  <p className="home-welcome-subtitle">安全 · 高效 · 极简的一站式数据管理平台</p>
                </div>
              </div>

              <div className="home-feature-list">
                {features.map((item, index) => (
                  <div className="home-feature-item" key={index}>
                    <span className="home-feature-emoji">{item.emoji}</span>
                    <span className="home-feature-text">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="home-col-right">
              <div className="home-col-header">
                <div className="home-notes-icon">
                  <SafetyOutlined />
                </div>
                <h3 className="home-notes-title">本站须知</h3>
              </div>

              <div className="home-notes-list">
                {notes.map((item, index) => (
                  <div className="home-note-card" key={index}>
                    <div className="home-note-card-title">
                      <span className="home-note-card-icon">{item.icon}</span>
                      {item.title}
                    </div>
                    {item.desc && (
                      <div className="home-note-card-desc">{item.desc}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="home-footer">
            © {new Date().getFullYear()} Tomato 综合站 · All Rights Reserved
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
